import { Handle, Position } from '@xyflow/react';
import { Stack, SegmentedControl, Text } from '@mantine/core';
import { IconRecordMail } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { recordingWidgetCore, type RecordingConfig } from '@legacy-shared/widgets/core/recording';

export { type RecordingConfig } from '@legacy-shared/widgets/core/recording';

function RecordingNode({ id, data, selected }: WidgetNodeProps<RecordingConfig>) {
  return (
    <WidgetNodeShell
      icon={IconRecordMail}
      title={data.label || 'Recording'}
      subtitle={data.config.action === 'start' ? 'Start recording' : 'Stop recording'}
      configured={true}
      errors={data.errors}
      selected={selected}
      color="red"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="done" label="Done" offset="50%" nodeId={id} />
      <LabeledHandle type="source" position="right" id="error" label="Error" offset="50%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function RecordingConfigPanel({ config, onChange }: ConfigPanelProps<RecordingConfig>) {
  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Control call recording. Use Start to begin recording and Stop to end it.
      </Text>

      <div>
        <Text size="sm" fw={500} mb={4}>
          Action
        </Text>
        <SegmentedControl
          fullWidth
          value={config.action}
          onChange={(v) => onChange({ ...config, action: v as RecordingConfig['action'] })}
          data={[
            { value: 'start', label: 'Start Recording' },
            { value: 'stop', label: 'Stop Recording' },
          ]}
        />
      </div>
    </Stack>
  );
}

export const recordingWidget: WidgetDefinition<RecordingConfig> = {
  ...recordingWidgetCore,
  icon: 'IconRecordMail',

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [
      { id: 'done', type: 'success', label: 'Done', position: 'bottom' },
      { id: 'error', type: 'failure', label: 'Error', position: 'right' },
    ],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: RecordingNode,
    configPanel: RecordingConfigPanel,
    preview: (config) => (config.action === 'start' ? 'Start recording' : 'Stop recording'),
  },

  validation: {
    ...recordingWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
